if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mainImageNavPrev: this.querySelector('.main-image-nav-button--prev'),
          mainImageNavNext: this.querySelector('.main-image-nav-button--next')
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Initialize viewer event listeners
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }
        
        // Initialize thumbnail navigation if thumbnails exist
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }
        
        // Initialize main image navigation buttons with iPhone-specific handling
        this.initializeMainImageNavigation();
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initializeMainImageNavigation() {
        // Re-query elements to ensure they exist (especially important for iPhone)
        const mainImageNavPrev = this.querySelector('.main-image-nav-button--prev');
        const mainImageNavNext = this.querySelector('.main-image-nav-button--next');
        
        console.log('Media Gallery Debug - Found navigation buttons:', {
          prev: !!mainImageNavPrev,
          next: !!mainImageNavNext,
          isIPhone: /iPad|iPhone|iPod/.test(navigator.userAgent)
        });
        
        if (mainImageNavPrev && mainImageNavNext) {
          // Update elements object
          this.elements.mainImageNavPrev = mainImageNavPrev;
          this.elements.mainImageNavNext = mainImageNavNext;
          
          // Add event listeners with iPhone-specific handling
          mainImageNavPrev.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Previous button clicked');
            this.navigateMainImage('previous');
          });
          
          mainImageNavNext.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Next button clicked');
            this.navigateMainImage('next');
          });
          
          // Force visibility for iPhone
          mainImageNavPrev.style.display = 'flex';
          mainImageNavNext.style.display = 'flex';
          mainImageNavPrev.style.visibility = 'visible';
          mainImageNavNext.style.visibility = 'visible';
          mainImageNavPrev.style.opacity = '1';
          mainImageNavNext.style.opacity = '1';
          
          console.log('Navigation buttons initialized successfully');
          
          // Additional iPhone-specific fixes
          setTimeout(() => {
            if (mainImageNavPrev && mainImageNavNext) {
              mainImageNavPrev.style.zIndex = '10000';
              mainImageNavNext.style.zIndex = '10000';
              mainImageNavPrev.style.pointerEvents = 'auto';
              mainImageNavNext.style.pointerEvents = 'auto';
              
              // Ensure SVG icons are visible
              const prevSvg = mainImageNavPrev.querySelector('.svg-wrapper svg');
              const nextSvg = mainImageNavNext.querySelector('.svg-wrapper svg');
              
              if (prevSvg) {
                prevSvg.style.display = 'block';
                prevSvg.style.visibility = 'visible';
                prevSvg.style.opacity = '1';
              }
              
              if (nextSvg) {
                nextSvg.style.display = 'block';
                nextSvg.style.visibility = 'visible';
                nextSvg.style.opacity = '1';
              }
              
              console.log('iPhone-specific fixes applied');
            }
          }, 100);
        } else {
          console.log('Navigation buttons not found, retrying...');
          // Retry initialization after a short delay (for iPhone)
          setTimeout(() => {
            this.initializeMainImageNavigation();
          }, 500);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      navigateMainImage(direction) {
        // Try to get thumbnails, but fallback to viewer if thumbnails don't exist
        const thumbnailsContainer = this.elements.thumbnails;
        const viewerContainer = this.elements.viewer;
        
        if (!viewerContainer) return;
        
        let mediaItems = [];
        let currentMediaId = '';
        
        // Get current active media
        const activeMedia = viewerContainer.querySelector('.is-active');
        if (activeMedia && activeMedia.dataset.mediaId) {
          currentMediaId = activeMedia.dataset.mediaId;
        }
        
        // Try to get media items from thumbnails first, then fallback to viewer
        if (thumbnailsContainer) {
          mediaItems = Array.from(thumbnailsContainer.querySelectorAll('[data-target]'));
        } else {
          // Fallback: get media items directly from viewer
          mediaItems = Array.from(viewerContainer.querySelectorAll('[data-media-id]')).map(item => ({
            dataset: { target: item.dataset.mediaId }
          }));
        }
        
        if (mediaItems.length <= 1) return;
        
        // Find current index
        const currentIndex = mediaItems.findIndex(item => item.dataset.target === currentMediaId);
        
        let targetIndex;
        if (direction === 'previous') {
          targetIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
        } else {
          targetIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
        }
        
        const targetItem = mediaItems[targetIndex];
        if (targetItem && targetItem.dataset.target) {
          this.setActiveMedia(targetItem.dataset.target, false);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset?.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
